/**
 * Documents – search documents using searchDocuments API.
 */
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../components/shared/PageHeader';
import Modal from '../../components/shared/Modal';
import ModalFormActions from '../../components/shared/ModalFormActions';
import { useActiveRepositories } from '../../hooks/edms/useActiveRepositories';
import { searchDocuments, getDocumentUrl, getDocumentDownload, uploadDocument } from '../../api/edms/documents';
import { getFolderTree, flattenFolderTree } from '../../api/edms/folders';
import { getActiveDocumentTypes } from '../../api/edms/lookups';
import { FileText, Search, Eye, Download, ExternalLink, X, Upload, Trash2 } from 'lucide-react';
import DataTable from '../../components/shared/DataTable';
import EmptyState from '../../components/shared/EmptyState';
import Pagination from '../../components/shared/Pagination';
import SuccessDialog from '../../components/shared/SuccessDialog';

const APP_ID = 'documents-management';
const APP_NAME = 'Documents Management';
const MODULE = 'Documents';

const GROUP_CATEGORY_OPTIONS = [
  { value: 'Confidential', label: 'Confidential' },
  { value: 'General', label: 'General' },
  { value: 'Personal', label: 'Personal' },
];
const GROUP_SUBCATEGORY_OPTIONS = [
  { value: 'Staff', label: 'Staff' },
  { value: 'Member', label: 'Member' },
];

const EdmsDocuments = () => {
  const [q, setQ] = useState('');
  const [repository, setRepository] = useState('');
  const [documentTypeId, setDocumentTypeId] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [documentViewer, setDocumentViewer] = useState(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadDocumentTypeId, setUploadDocumentTypeId] = useState('');
  const [uploadRepository, setUploadRepository] = useState('');
  const [uploadGroupCategory, setUploadGroupCategory] = useState('');
  const [uploadGroupSubCategory, setUploadGroupSubCategory] = useState('');
  const [uploadFolderId, setUploadFolderId] = useState('');
  const [documentUploadedSuccess, setDocumentUploadedSuccess] = useState({ isOpen: false, message: '' });

  const uploadFileInputRef = useRef(null);
  const [uploadPreviewUrl, setUploadPreviewUrl] = useState(null);
  const [uploadTextPreview, setUploadTextPreview] = useState(null);

  useEffect(() => {
    if (!uploadFile) {
      setUploadPreviewUrl(null);
      setUploadTextPreview(null);
      return;
    }
    const url = URL.createObjectURL(uploadFile);
    setUploadPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [uploadFile]);

  useEffect(() => {
    if (!uploadFile) {
      setUploadTextPreview(null);
      return;
    }
    const type = (uploadFile.type || '').toLowerCase();
    const name = (uploadFile.name || '').toLowerCase();
    const isText =
      type.startsWith('text/') ||
      type === 'application/json' ||
      type === 'application/xml' ||
      /\.(txt|json|xml|csv|md|log|ini|cfg|conf)$/.test(name);
    if (!isText) {
      setUploadTextPreview(null);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setUploadTextPreview(reader.result);
    reader.onerror = () => setUploadTextPreview(null);
    reader.readAsText(uploadFile, 'UTF-8');
  }, [uploadFile]);

  const clearUploadFile = useCallback(() => {
    setUploadFile(null);
    setUploadTextPreview(null);
    if (uploadFileInputRef.current) uploadFileInputRef.current.value = '';
  }, []);

  const queryClient = useQueryClient();
  const { repositories } = useActiveRepositories({ enabled: true });
  const { data: documentTypesRes } = useQuery({
    queryKey: ['edms', 'documentTypes', 'active'],
    queryFn: getActiveDocumentTypes,
  });
  const documentTypes = Array.isArray(documentTypesRes?.data) ? documentTypesRes.data : documentTypesRes ?? [];

  const uploadFolderTreeParams =
    uploadModalOpen && uploadRepository && uploadGroupCategory
      ? {
          repository: uploadRepository,
          groupCategory: uploadGroupCategory,
          ...(uploadGroupSubCategory?.trim() && { groupSubCategory: uploadGroupSubCategory.trim() }),
        }
      : null;
  const { data: uploadFolderTreeData } = useQuery({
    queryKey: ['edms', 'folders', 'tree', uploadFolderTreeParams],
    queryFn: () => getFolderTree(uploadFolderTreeParams || {}),
    enabled: !!uploadFolderTreeParams,
  });
  const uploadFoldersList = useMemo(
    () => (uploadFolderTreeData ? flattenFolderTree(uploadFolderTreeData) : []),
    [uploadFolderTreeData]
  );

  const uploadDocMutation = useMutation({
    mutationFn: uploadDocument,
    onSuccess: () => {
      setUploadModalOpen(false);
      setUploadFile(null);
      setUploadTitle('');
      setUploadDocumentTypeId('');
      setUploadFolderId('');
      setUploadRepository('');
      setUploadGroupCategory('');
      setUploadGroupSubCategory('');
      queryClient.invalidateQueries({ queryKey: ['edms', 'documents', 'search'] });
      setDocumentUploadedSuccess({ isOpen: true, message: 'Document uploaded successfully.' });
    },
  });

  const searchParams = submitted
    ? { q: q.trim() || undefined, repository: repository || undefined, documentTypeId: documentTypeId || undefined, page, limit }
    : null;

  const { data: searchResult, isLoading, isError, error } = useQuery({
    queryKey: ['edms', 'documents', 'search', searchParams],
    queryFn: () => searchDocuments(searchParams || {}),
    enabled: searchParams !== null,
  });

  const { data: documentViewUrlData } = useQuery({
    queryKey: ['documentViewUrl', documentViewer?.id],
    queryFn: () => getDocumentUrl(documentViewer.id),
    enabled: !!documentViewer?.id,
    retry: false,
    staleTime: 60 * 1000,
  });
  const documentViewUrl = useMemo(() => {
    if (!documentViewer?.id) return null;
    const d = documentViewUrlData;
    const raw =
      (typeof d === 'string' && d) ? d
        : d?.url ?? d?.viewUrl ?? d?.fileUrl
        ?? (typeof d?.data === 'string' ? d.data : null)
        ?? d?.data?.url ?? d?.data?.viewUrl
        ?? null;
    if (typeof raw === 'string' && raw.trim()) {
      const url = raw.trim();
      if (url.startsWith('http://') || url.startsWith('https://')) return url;
      return `${window.location.origin}${url.startsWith('/') ? '' : '/'}${url}`;
    }
    const base =
      (typeof process !== 'undefined' && process.env?.REACT_APP_API_BASE_URL) ||
      (typeof window !== 'undefined' && window.location?.origin) ||
      '';
    const path = `/api/documents/${documentViewer.id}/view`;
    return base ? `${base.replace(/\/$/, '')}${path}` : path;
  }, [documentViewer?.id, documentViewUrlData]);

  const handleSearch = (e) => {
    e?.preventDefault();
    setSubmitted(true);
    setPage(1);
  };

  const rows =
    Array.isArray(searchResult?.data)
      ? searchResult.data
      : Array.isArray(searchResult?.data?.documents)
      ? searchResult.data.documents
      : Array.isArray(searchResult?.data?.items)
      ? searchResult.data.items
      : Array.isArray(searchResult)
      ? searchResult
      : searchResult?.documents ?? [];

  const rawPagination = searchResult?.pagination ?? searchResult?.data?.pagination;
  const pagination = rawPagination
    ? {
        currentPage: rawPagination.page ?? rawPagination.currentPage ?? 1,
        totalPages: (rawPagination.pages ?? rawPagination.totalPages ?? Math.ceil((rawPagination.total ?? 0) / (rawPagination.limit ?? limit))) || 1,
        hasPrevPage: (rawPagination.page ?? 1) > 1,
        hasNextPage: (rawPagination.page ?? 1) < (rawPagination.pages ?? rawPagination.totalPages ?? 1),
      }
    : null;

  const handleDownload = useCallback(async (doc) => {
    const id = doc._id ?? doc.id;
    if (!id) return;
    try {
      const response = await getDocumentDownload(id);
      const blob = response?.data ?? response;
      if (!blob || !(blob instanceof Blob)) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.title ?? doc.name ?? doc.fileName ?? doc.originalName ?? 'document';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    }
  }, []);

  const handleView = useCallback(async (doc) => {
    const id = doc._id ?? doc.id;
    if (!id) return;
    try {
      const res = await getDocumentUrl(id);
      const data = res?.data ?? res;
      const viewUrl =
        data?.url ?? data?.viewUrl ?? data?.fileUrl ?? data?.data?.url ?? (typeof data === 'string' ? data : null);
      if (viewUrl) {
        window.open(viewUrl, '_blank', 'noopener,noreferrer');
      } else {
        const base = process.env.REACT_APP_API_BASE_URL || window.location.origin;
        window.open(`${base}/api/documents/${id}/view`, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      console.error('View failed:', err);
      const base = process.env.REACT_APP_API_BASE_URL || window.location.origin;
      window.open(`${base}/api/documents/${id}/view`, '_blank', 'noopener,noreferrer');
    }
  }, []);

  const openViewerModal = useCallback((doc) => {
    const id = doc._id ?? doc.id;
    if (!id) return;
    setDocumentViewer({
      id,
      name: doc.title ?? doc.name ?? doc.fileName ?? doc.originalName ?? 'Document',
      mimetype: doc.mimetype ?? doc.mimeType ?? doc.contentType,
    });
  }, []);

  const columns = [
    {
      key: 'icon',
      header: '',
      headerClassName: 'w-12',
      className: 'w-12',
      render: (row) => (
        <button
          type="button"
          onClick={() => openViewerModal(row)}
          title="View document"
          className="p-1.5 rounded text-amber-500 hover:bg-amber-50 hover:text-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-400/50"
          aria-label="View document"
        >
          <FileText className="h-5 w-5" aria-hidden />
        </button>
      ),
    },
    {
      key: 'name',
      header: 'Name',
      render: (row) => (
        <span className="font-medium text-slate-800">
          {row.title ?? row.name ?? row.fileName ?? row.originalName ?? '—'}
        </span>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      render: (row) => (
        <span className="text-slate-600">
          {row.documentType?.name ?? row.documentTypeName ?? row.mimetype ?? row.mimeType ?? '—'}
        </span>
      ),
    },
    {
      key: 'repository',
      header: 'Repository',
      render: (row) => <span className="text-slate-600">{row.repository ?? row.repo ?? '—'}</span>,
    },
    {
      key: 'createdAt',
      header: 'Created',
      render: (row) => {
        const raw = row.createdAt ?? row.created_at;
        return <span className="text-slate-500 text-xs">{raw ? new Date(raw).toLocaleString() : '—'}</span>;
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (row) => (
        <div className="inline-flex items-center gap-1">
          <button
            type="button"
            title="View"
            onClick={() => handleView(row)}
            className="p-1.5 rounded text-slate-600 hover:bg-slate-100 hover:text-slate-800"
            aria-label="View document"
          >
            <Eye className="h-4 w-4" aria-hidden />
          </button>
          <button
            type="button"
            title="Download"
            onClick={() => handleDownload(row)}
            className="p-1.5 rounded text-slate-600 hover:bg-slate-100 hover:text-slate-800"
            aria-label="Download document"
          >
            <Download className="h-4 w-4" aria-hidden />
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={`${APP_NAME} | ${MODULE}`}
        breadcrumbItems={[
          { label: APP_NAME, href: `/app/${APP_ID}` },
          { label: MODULE },
        ]}
      />
      <div className="rounded-2xl border border-cream/60 bg-white shadow-sm overflow-hidden">
        <div className="p-5 border-b border-cream/60">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">{MODULE}</h2>
          <form onSubmit={handleSearch} className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium text-slate-500 mb-1">Search</label>
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name, title..."
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                aria-label="Search query"
              />
            </div>
            <div className="min-w-[140px]">
              <label className="block text-xs font-medium text-slate-500 mb-1">Repository</label>
              <select
                value={repository}
                onChange={(e) => setRepository(e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                aria-label="Filter by repository"
              >
                <option value="">All</option>
                {repositories?.map((r) => (
                  <option key={r._id ?? r.name} value={r.name ?? ''}>
                    {r.name ?? r.pathSegment ?? r._id}
                  </option>
                ))}
              </select>
            </div>
            <div className="min-w-[160px]">
              <label className="block text-xs font-medium text-slate-500 mb-1">Document type</label>
              <select
                value={documentTypeId}
                onChange={(e) => setDocumentTypeId(e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                aria-label="Filter by document type"
              >
                <option value="">All</option>
                {documentTypes.map((dt) => (
                  <option key={dt._id ?? dt.id} value={dt._id ?? dt.id}>
                    {dt.name ?? dt.code ?? dt._id}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="submit"
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              <Search className="h-4 w-4" aria-hidden />
              Search
            </button>
            <div className="ml-auto">
              <button
                type="button"
                onClick={() => setUploadModalOpen(true)}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary/50"
                aria-label="Upload document"
              >
                <Upload className="h-4 w-4" aria-hidden />
                Upload
              </button>
            </div>
          </form>
        </div>

        <div className="p-5">
          {!submitted && (
            <div className="flex flex-col items-center justify-center py-12 text-slate-500">
              <Search className="h-12 w-12 text-slate-300 mb-3" aria-hidden />
              <p className="text-sm">Enter search terms and click Search to find documents.</p>
            </div>
          )}
          {submitted && (
            <>
              {isLoading && (
                <div className="flex items-center justify-center py-12">
                  <span className="text-sm text-slate-500">Searching…</span>
                </div>
              )}
              {isError && (
                <EmptyState
                  isError
                  title="Search failed"
                  description={error?.message ?? error?.response?.data?.message ?? 'Unable to search documents.'}
                />
              )}
              {!isLoading && !isError && (
                <>
                  {rows.length === 0 ? (
                    <EmptyState
                      title="No documents found"
                      description="Try different search terms or filters."
                      icon={<FileText className="h-12 w-12 text-slate-300" strokeWidth={1.25} />}
                    />
                  ) : (
                    <>
                      <DataTable
                        columns={columns}
                        rows={rows}
                        getRowKey={(row, i) => row._id ?? row.id ?? i}
                        emptyTitle="No results"
                        emptyMessage="No documents match your search."
                      />
                      {pagination && (
                        <div className="mt-3">
                          <Pagination pagination={pagination} onPageChange={(newPage) => setPage(newPage)} />
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Document viewer modal */}
      <Modal
        isOpen={!!documentViewer}
        onClose={() => setDocumentViewer(null)}
        title={documentViewer?.name ?? 'Document'}
        size="2xl"
        bodyClassName="p-0 flex flex-col min-h-[70vh]"
      >
        <div className="flex flex-1 flex-col min-h-0">
          {documentViewUrl ? (
            <>
              <div className="flex-1 min-h-0 rounded-b-lg overflow-hidden bg-slate-100">
                {(documentViewer?.mimetype ?? documentViewer?.mimeType ?? '').startsWith('image/') ? (
                  <img
                    src={documentViewUrl}
                    alt={documentViewer?.name ?? 'Document'}
                    className="w-full h-full object-contain"
                    style={{ minHeight: '60vh' }}
                  />
                ) : (
                  <iframe
                    title={documentViewer?.name ?? 'Document viewer'}
                    src={documentViewUrl}
                    className="w-full h-full min-h-[70vh] border-0"
                  />
                )}
              </div>
              <div className="flex items-center justify-end gap-2 p-3 border-t border-slate-200 bg-white">
                <a
                  href={documentViewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  <ExternalLink className="h-4 w-4" />
                  Open in new tab
                </a>
                <button
                  type="button"
                  onClick={() => setDocumentViewer(null)}
                  className="inline-flex items-center gap-2 rounded-lg bg-slate-800 px-3 py-2 text-sm font-medium text-white hover:bg-slate-700"
                >
                  <X className="h-4 w-4" />
                  Close
                </button>
              </div>
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center py-16 text-slate-500">
              Loading viewer…
            </div>
          )}
        </div>
      </Modal>

      {/* Upload document modal */}
      <Modal
        isOpen={uploadModalOpen}
        onClose={() => {
          if (!uploadDocMutation.isPending) {
            setUploadModalOpen(false);
            setUploadFile(null);
            setUploadTitle('');
            setUploadDocumentTypeId('');
            setUploadFolderId('');
            setUploadRepository('');
            setUploadGroupCategory('');
            setUploadGroupSubCategory('');
          }
        }}
        title="Upload document"
        size="xl"
      >
        <form
          id="upload-document-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!uploadFile || !uploadFolderId) return;
            const formData = new FormData();
            formData.append('file', uploadFile);
            formData.append('folderId', uploadFolderId);
            if (uploadTitle.trim()) formData.append('title', uploadTitle.trim());
            if (uploadDocumentTypeId) formData.append('documentTypeId', uploadDocumentTypeId);
            uploadDocMutation.mutate(formData);
          }}
          className="flex flex-col gap-4"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 min-h-0">
            {/* Left: form fields */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Repository</label>
                <select
                  value={uploadRepository}
                  onChange={(e) => {
                    setUploadRepository(e.target.value);
                    setUploadFolderId('');
                  }}
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                  required
                >
                  <option value="">Select repository</option>
                  {repositories?.map((r) => (
                    <option key={r._id ?? r.name} value={r.name ?? ''}>
                      {r.name ?? r.pathSegment ?? r._id}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Group category</label>
                <select
                  value={uploadGroupCategory}
                  onChange={(e) => {
                    setUploadGroupCategory(e.target.value);
                    setUploadGroupSubCategory('');
                    setUploadFolderId('');
                  }}
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                  required
                >
                  <option value="">Select category</option>
                  {GROUP_CATEGORY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              {uploadGroupCategory === 'Personal' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Under (Personal)</label>
                  <select
                    value={uploadGroupSubCategory}
                    onChange={(e) => {
                      setUploadGroupSubCategory(e.target.value);
                      setUploadFolderId('');
                    }}
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                  >
                    <option value="">Select</option>
                    {GROUP_SUBCATEGORY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Folder</label>
                <select
                  value={uploadFolderId}
                  onChange={(e) => setUploadFolderId(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                  required
                  disabled={!uploadRepository || !uploadGroupCategory}
                >
                  <option value="">Select folder</option>
                  {uploadFoldersList.map((f) => (
                    <option key={f._id ?? f.id} value={f._id ?? f.id}>
                      {f.folderName ?? f.name ?? f.folderNumber ?? f._id}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">File</label>
                <div className="flex gap-2">
                  <input
                    ref={uploadFileInputRef}
                    type="file"
                    onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
                    className="flex-1 min-w-0 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                    required
                  />
                  {uploadFile && (
                    <button
                      type="button"
                      onClick={clearUploadFile}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-red-300 bg-white px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500/30"
                      aria-label="Clear file"
                    >
                      <Trash2 className="h-4 w-4" aria-hidden />
                      Clear
                    </button>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Title (optional)</label>
                <input
                  type="text"
                  value={uploadTitle}
                  onChange={(e) => setUploadTitle(e.target.value)}
                  placeholder="Document title"
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-700 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Document type (optional)</label>
                <select
                  value={uploadDocumentTypeId}
                  onChange={(e) => setUploadDocumentTypeId(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                >
                  <option value="">None</option>
                  {documentTypes.map((dt) => (
                    <option key={dt._id ?? dt.id} value={dt._id ?? dt.id}>
                      {dt.name ?? dt.code ?? dt._id}
                    </option>
                  ))}
                </select>
              </div>
              {uploadDocMutation.isError && (
                <p className="text-sm text-red-600">
                  {uploadDocMutation.error?.message ?? uploadDocMutation.error?.response?.data?.message ?? 'Upload failed'}
                </p>
              )}
              <ModalFormActions
                formId="upload-document-form"
                onCancel={() => {
                  setUploadModalOpen(false);
                  setUploadFile(null);
                  setUploadTitle('');
                  setUploadDocumentTypeId('');
                  setUploadFolderId('');
                  setUploadRepository('');
                  setUploadGroupCategory('');
                  setUploadGroupSubCategory('');
                }}
                submitText="Upload"
                submittingText="Uploading…"
                isSubmitting={uploadDocMutation.isPending}
                submitDisabled={!uploadFile || !uploadFolderId}
              />
            </div>

            {/* Right: preview */}
            <div className="rounded-xl border border-cream/60 bg-slate-50/50 flex flex-col min-h-[420px] lg:min-h-[70vh]">
              <p className="text-xs font-medium text-slate-500 px-4 pt-3 pb-2">Preview</p>
              {!uploadFile ? (
                <div className="flex-1 flex items-center justify-center p-6 text-slate-400">
                  <div className="text-center">
                    <FileText className="h-12 w-12 mx-auto mb-2 text-slate-300" aria-hidden />
                    <p className="text-sm">Select a file to preview</p>
                  </div>
                </div>
              ) : (uploadFile.type || '').startsWith('image/') && uploadPreviewUrl ? (
                <div className="flex-1 flex justify-center items-start p-4 overflow-auto">
                  <img
                    src={uploadPreviewUrl}
                    alt="Document preview"
                    className="max-h-full max-w-full object-contain rounded-lg"
                  />
                </div>
              ) : (uploadFile.type || '').includes('pdf') && uploadPreviewUrl ? (
                <div className="flex-1 min-h-0 flex flex-col p-2">
                  <iframe
                    title="Document preview"
                    src={uploadPreviewUrl}
                    className="flex-1 w-full min-h-[70vh] border-0 rounded-lg bg-white"
                  />
                </div>
              ) : uploadTextPreview !== null ? (
                <div className="flex-1 min-h-0 overflow-auto p-4">
                  <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans bg-white rounded-lg p-4 border border-cream/60 max-h-full overflow-auto">
                    {uploadTextPreview}
                  </pre>
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-center p-6">
                  <div className="flex items-center gap-3 rounded-lg bg-white p-4 border border-cream/60 text-sm text-slate-600">
                    <FileText className="h-10 w-10 shrink-0 text-slate-400" aria-hidden />
                    <div>
                      <p className="font-medium text-slate-700">{uploadFile.name}</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {(uploadFile.size / 1024).toFixed(1)} KB
                        {uploadFile.type ? ` · ${uploadFile.type}` : ''}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">Preview not available for this file type.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </form>
      </Modal>

      <SuccessDialog
        isOpen={documentUploadedSuccess.isOpen}
        title="Success"
        message={documentUploadedSuccess.message}
        onClose={() => setDocumentUploadedSuccess({ isOpen: false, message: '' })}
        variant="success"
      />
    </>
  );
};

export default EdmsDocuments;
